import { useMutation, useQuery, ApolloCache } from '@apollo/client'
import { useEffect, useState } from 'react'
import upsertShortUrlMutation from '../mutations/upsertShortUrl'
import getAllShortUrlQuery from '../queries/getAllShortUrls'
import useToggle from '../../../hooks/useToggle'
import deleteShortUrlMutation from '../mutations/deleteShortUrl'
import useToast, { ToastType } from '../../../hooks/useToast'

const DEFAULT_PAGE_SIZE = 30

export interface ShortenedUrl {
  id?: string
  sourceUrl?: string
  targetUrl: string
}

export interface PaginationInfo {
  page: number
  size: number
  totalPages: number
  totalHits: number
  hasMore: boolean
}
const useUrlShortener = () => {
  const [
    shortenedUrl,
    setShortenedUrl
  ] = useState<Partial<ShortenedUrl> | null>(null)
  const [
    selectedForDeletion,
    setSelectedForDeletion
  ] = useState<ShortenedUrl | null>(null)
  const { isOn, turnOn, turnOff } = useToggle()
  const { showToast } = useToast()
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    size: DEFAULT_PAGE_SIZE,
    totalPages: 1,
    totalHits: 0,
    hasMore: true
  })

  const [upsertShortUrl, { loading: loadingMutation }] = useMutation(
    upsertShortUrlMutation
  )
  const [deleteShortUrl, { loading: loadingDeletion }] = useMutation(
    deleteShortUrlMutation
  )

  interface ShortUrlResponse {
    allShortUrls: {
      hits: ShortenedUrl[]
      page: number
      size: number
      totalPages: number
      totalHits: number
    }
  }

  const { data, loading, fetchMore } = useQuery<ShortUrlResponse>(
    getAllShortUrlQuery,
    {
      notifyOnNetworkStatusChange: true,
      variables: { page: 1, size: DEFAULT_PAGE_SIZE },
      onCompleted: response => {
        if (response?.allShortUrls) {
          const { page, size, totalPages, totalHits } = response.allShortUrls
          setPagination(prev => ({
            ...prev,
            page,
            size,
            totalPages,
            totalHits,
            hasMore: page < totalPages
          }))
        }
      }
    }
  )

  const findById = (selectedId: string) =>
    data?.allShortUrls?.hits.find(({ id }) => id === selectedId)

  useEffect(() => {
    if (!isOn) {
      setShortenedUrl(null)
    }
  }, [isOn])

  useEffect(() => {
    if (!isOn && shortenedUrl?.id) {
      turnOn()
    }
  }, [shortenedUrl?.id])

  const selectUrl = (selectedId: string) => {
    const url = findById(selectedId)
    if (url) {
      setShortenedUrl(url)
    }
  }

  const selectForDeletion = (selectedId: string) => {
    const url = findById(selectedId)
    if (url) {
      setSelectedForDeletion(url)
    }
  }

  const cancelDeletion = () => setSelectedForDeletion(null)

  const confirmDeletion = () => {
    if (!selectedForDeletion?.id) return

    deleteShortUrl({
      variables: { id: selectedForDeletion.id },
      update: (cache: ApolloCache<any>) => {
        cache.modify({
          fields: {
            allShortUrls(existingData = { hits: [] }, { readField }) {
              const newData = existingData.hits.filter(
                item => readField('id', item) !== selectedForDeletion.id
              )
              return { ...existingData, hits: newData }
            }
          }
        })
      },
      onCompleted: () => {
        showToast('URL deleted successfully', ToastType.INFO, 3000)
        setSelectedForDeletion(null)
      },
      onError: error => {
        showToast(error.message, ToastType.ERROR, 8000)
      }
    })
  }

  const setTargetUrl = (value: string) => {
    setShortenedUrl(prev => ({
      ...(prev || {}),
      targetUrl: value
    }))
  }

  const setSourceUrl = (value: string) => {
    setShortenedUrl(prev => ({
      ...(prev || {}),
      sourceUrl: value
    }))
  }

  const loadMore = () => {
    if (!pagination.hasMore || loading) return

    const nextPage = pagination.page + 1
    fetchMore<ShortUrlResponse>({
      variables: { page: nextPage, size: pagination.size },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev

        const newHits = fetchMoreResult.allShortUrls.hits
        const mergedHits = [...(prev.allShortUrls?.hits || []), ...newHits]

        setPagination(prev => ({
          ...prev,
          page: nextPage,
          hasMore: nextPage < prev.totalPages
        }))

        return {
          ...prev,
          allShortUrls: {
            ...fetchMoreResult.allShortUrls,
            hits: mergedHits
          }
        }
      }
    })
  }

  const upsertShortUrlWithVariables = () => {
    const isUpdate = !!shortenedUrl?.id

    upsertShortUrl({
      variables: {
        targetUrl: shortenedUrl?.targetUrl,
        id: shortenedUrl?.id
      },
      update: (cache, { data }) => {
        if (!data?.upsertShortUrl) return

        // For new URLs, we need to update the cache to include the new item
        if (!isUpdate) {
          const newUrl = {
            ...data.upsertShortUrl,
            __typename: 'ShortUrl'
          }

          const queryData = cache.readQuery<ShortUrlResponse>({
            query: getAllShortUrlQuery,
            variables: { page: 1, size: DEFAULT_PAGE_SIZE }
          })

          if (queryData?.allShortUrls) {
            const newData = {
              ...queryData,
              allShortUrls: {
                ...queryData.allShortUrls,
                hits: [newUrl, ...queryData.allShortUrls.hits],
                totalHits: queryData.allShortUrls.totalHits + 1
              }
            }

            cache.writeQuery({
              query: getAllShortUrlQuery,
              variables: { page: 1, size: DEFAULT_PAGE_SIZE },
              data: newData
            })
          }
        }
      },
      onCompleted: data => {
        const { id, sourceUrl, targetUrl } = data.upsertShortUrl
        setShortenedUrl({ id, sourceUrl, targetUrl })
        showToast(
          isUpdate
            ? 'URL updated successfully'
            : 'Your short URL was generated',
          ToastType.INFO,
          3000
        )
        turnOff()
      },
      onError: error => {
        showToast(
          error.message ||
            (isUpdate ? 'Failed to update URL' : 'Failed to create URL'),
          ToastType.ERROR,
          8000
        )
      }
    })
  }

  return {
    confirmDeletion,
    cancelDeletion,
    selectForDeletion,
    selectedForDeletion,
    isOn,
    turnOff,
    turnOn,
    targetUrl: shortenedUrl?.targetUrl ?? '',
    setTargetUrl,
    sourceUrl: shortenedUrl?.sourceUrl ?? '',
    setSourceUrl,
    selectUrl,
    upsertShortUrl: upsertShortUrlWithVariables,
    loadingData: loading,
    loadingUpsertion: loadingMutation,
    loadingDeletion,
    loadMore,
    pagination,
    allShortUrls: data?.allShortUrls?.hits || [],
    shortUrls: data?.allShortUrls?.hits || []
  }
}

export default useUrlShortener
